// The browser's half of the session diagnostic log (qa-runs 1227). Nothing here runs while a table is off: `start` adds
// every listener and timer, `stop` takes each away, and only `watchDiagnostics` and the early check sit waiting.
// Rows post with the server's ticket and no bearer, so the same call works from `sendBeacon` as the page goes; that is
// why they are not sent through `authFetch`. A row that cannot be sent is lost, and play never waits for one.
import { useSyncExternalStore } from 'react';
import type { Socket } from 'socket.io-client';
import { DIAG_LIMITS, TABLE_DIAG, TABLE_LATENCY, diagHash, diagPayload, type DiagState } from '@dragons-table/vtt-shared';
import { rendererName } from './board/perf';
import { capture, captureForget, captureStop, captureTo, onError, type Captured } from './diagnosticsBoot';
import { authFetch, hasSession } from './session';

const POST_URL = '/api/diagnostics/client';
const SEND_MS = 5000;
const QUEUE_MAX = 3000;
const BEACON_BYTES = 60_000;

/** A control's name for the log: its test id or label; its words only outside the places a person writes privately. */
const CONTROL = 'button,a[href],[role=button],[role=menuitem],[role=menuitemcheckbox],[role=menuitemradio],[role=tab],[role=switch],[role=checkbox],[role=radio],[role=option],summary,label,input,select,textarea,[data-testid]';
const WORDED = 'button,a[href],[role=button],[role=menuitem],[role=menuitemcheckbox],[role=menuitemradio],[role=tab],summary';
const PRIVATE = '[data-testid^="notebook"],[data-testid^="dm-screen"],[data-testid*="wiki"],[contenteditable]';
const LIVE = '[role=alert],[role=status],[aria-live]';

export function controlName(el: Element): { name: string; tag: string } {
  const tag = el.tagName.toLowerCase();
  const name = el.getAttribute('data-testid') ?? el.getAttribute('aria-label')
    ?? (el.matches(WORDED) && !el.closest(PRIVATE) ? (el.textContent ?? '').replace(/\s+/g, ' ').trim().slice(0, 60) : '');
  return { name, tag };
}
const editable = (t: EventTarget | null): boolean => t instanceof Element && !!t.closest('input,textarea,select,[contenteditable]');

interface Row { kind: string; t: number; socket?: string; payload: unknown }
let recording: { campaignId: string; ticket: string } | null = null;
let queue: Row[] = [];
let cleanups: Array<() => void> = [];
let socketNow: Socket | null = null;
let known = false;
const listeners = new Set<() => void>();
const changed = () => { for (const l of listeners) l(); };

const add = (c: Captured) => {
  if (queue.length >= QUEUE_MAX) return;
  queue.push({ kind: c.kind, t: c.t, ...(socketNow?.id ? { socket: socketNow.id } : {}), payload: c.payload });
};
const bodyOf = (rows: Row[], ticket: string) => {
  const at = Date.now();
  return JSON.stringify({ ticket, rows: rows.map((r) => ({ kind: r.kind, ago: at - r.t, ...(r.socket ? { socket: r.socket } : {}), payload: JSON.parse(diagPayload(r.payload).json) })) });
};
function send(realFetch: typeof fetch): void {
  if (!recording || queue.length === 0) return;
  const rows = queue.splice(0, DIAG_LIMITS.clientRowsPerPost);
  void realFetch(POST_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: bodyOf(rows, recording.ticket) })
    .then((res) => { if (res.status === 409) stop(); }, () => undefined);
}
/** The last of the rows, as the page goes. A beacon holds about 64 KB, so the newest that fit are sent. */
function beacon(): void {
  if (!recording || queue.length === 0) return;
  let rows = queue.splice(0);
  let body = bodyOf(rows, recording.ticket);
  while (body.length > BEACON_BYTES && rows.length > 1) { rows = rows.slice(Math.ceil(rows.length / 2)); body = bodyOf(rows, recording.ticket); }
  navigator.sendBeacon(POST_URL, new Blob([body], { type: 'application/json' }));
}

function on<T extends EventTarget>(target: T, event: string, fn: (e: never) => void, options?: AddEventListenerOptions | boolean): void {
  target.addEventListener(event, fn as EventListener, options);
  cleanups.push(() => target.removeEventListener(event, fn as EventListener, options));
}
function every(ms: number, fn: () => void): void {
  const timer = setInterval(fn, ms);
  cleanups.push(() => clearInterval(timer));
}

function device(): void {
  const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  capture('client.device', {
    agent: navigator.userAgent, screen: `${screen.width}x${screen.height}`, window: `${innerWidth}x${innerHeight}`, ratio: devicePixelRatio,
    pointer: matchMedia('(pointer: coarse)').matches ? 'coarse' : matchMedia('(pointer: fine)').matches ? 'fine' : 'none', touch: navigator.maxTouchPoints,
    renderer: rendererName(), build: __VTT_COMMIT__, load: nav?.type ?? null,
  });
}

function watchPerformance(): void {
  let frames = 0;
  let long = 0;
  let longest = 0;
  let raf = requestAnimationFrame(function tick() { frames += 1; raf = requestAnimationFrame(tick); });
  cleanups.push(() => cancelAnimationFrame(raf));
  if (typeof PerformanceObserver !== 'undefined' && PerformanceObserver.supportedEntryTypes?.includes('longtask')) {
    const seen = new PerformanceObserver((list) => { for (const e of list.getEntries()) { long += 1; longest = Math.max(longest, e.duration); } });
    seen.observe({ entryTypes: ['longtask'] });
    cleanups.push(() => seen.disconnect());
  }
  every(60_000, () => {
    const memory = (performance as Performance & { memory?: { usedJSHeapSize: number; jsHeapSizeLimit: number } }).memory;
    capture('client.perf', { fps: Math.round(frames / 60), longTasks: long, longestMs: Math.round(longest), visible: document.visibilityState === 'visible', ...(memory ? { heapMb: Math.round(memory.usedJSHeapSize / 1048576), heapLimitMb: Math.round(memory.jsHeapSizeLimit / 1048576) } : {}) });
    frames = 0; long = 0; longest = 0;
  });
}

/** Every line a live region shows: the notices and refusals on screen, in their own words. */
function watchNotices(): void {
  const last = new WeakMap<Element, string>();
  const seen = new MutationObserver((changes) => {
    for (const m of changes) {
      const base = m.target instanceof Element ? m.target : m.target.parentElement;
      const found = new Set<Element>();
      const region = base?.closest(LIVE);
      if (region) found.add(region);
      m.addedNodes.forEach((n) => {
        if (!(n instanceof Element)) return;
        if (n.matches(LIVE)) found.add(n);
        n.querySelectorAll(LIVE).forEach((r) => found.add(r));
      });
      for (const r of found) {
        if (r.closest(PRIVATE)) continue;
        const words = (r.textContent ?? '').replace(/\s+/g, ' ').trim().slice(0, 300);
        if (words && last.get(r) !== words) { last.set(r, words); capture('client.notice', { words, role: r.getAttribute('role') }); }
      }
    }
  });
  seen.observe(document.body, { childList: true, subtree: true, characterData: true });
  cleanups.push(() => seen.disconnect());
}

function watchFetches(): typeof fetch {
  const real = window.fetch;
  const bound = real.bind(window);
  window.fetch = (input, init) => {
    const started = performance.now();
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const path = url.split('?')[0];
    const method = init?.method ?? 'GET';
    return bound(input, init).then((res) => {
      if (!res.ok && path !== POST_URL) capture('client.fetch.failed', { method, path, status: res.status, ms: Math.round(performance.now() - started) });
      return res;
    }, (err: unknown) => {
      if (path !== POST_URL) capture('client.fetch.failed', { method, path, error: String(err), ms: Math.round(performance.now() - started) });
      throw err;
    });
  };
  cleanups.push(() => { window.fetch = real; });
  return bound;
}

function watchSocket(socket: Socket): void {
  const says = (event: string, fn: (...a: never[]) => void) => { socket.on(event, fn as never); cleanups.push(() => socket.off(event, fn as never)); };
  const state = (state: string, extra: object = {}) => capture('client.socket', { state, ...extra });
  says('connect', () => state('connected'));
  says('disconnect', (reason: string) => state('disconnected', { reason }));
  says('connect_error', (err: Error) => capture('client.socket.error', { message: err.message }));
  const mgr = socket.io;
  const attempt = (n: number) => state('reconnecting', { attempt: n });
  const back = (n: number) => state('reconnected', { attempts: n });
  mgr.on('reconnect_attempt', attempt); mgr.on('reconnect', back);
  cleanups.push(() => { mgr.off('reconnect_attempt', attempt); mgr.off('reconnect', back); });
  const notice = (event: string, ...args: unknown[]) => { if (event === 'table:notice') capture('client.notice', { words: (args[0] as { text?: unknown } | undefined)?.text }); };
  socket.onAny(notice);
  cleanups.push(() => socket.offAny(notice));
  every(30_000, () => {
    if (!socket.connected) return;
    const started = performance.now();
    socket.timeout(5000).emit(TABLE_LATENCY, (err: Error | null) => capture('client.latency', err ? { timedOut: true } : { ms: Math.round(performance.now() - started) }));
  });
}

function start(campaignId: string, ticket: string): void {
  if (recording) { recording = { campaignId, ticket }; return; }
  recording = { campaignId, ticket };
  const realFetch = watchFetches();
  captureTo(add);
  device();
  on(document, 'click', (e: MouseEvent) => {
    const el = e.target instanceof Element ? e.target.closest(CONTROL) : null;
    if (el) capture('client.click', controlName(el));
  }, true);
  on(document, 'keydown', (e: KeyboardEvent) => {
    if (e.repeat || editable(e.target) || ['Shift', 'Control', 'Alt', 'Meta'].includes(e.key)) return;
    capture('client.key', { key: e.key, ctrl: e.ctrlKey, alt: e.altKey, meta: e.metaKey, shift: e.shiftKey });
  }, true);
  on(document, 'submit', (e: Event) => {
    const form = e.target as Element;
    capture('client.form', { name: form.getAttribute('data-testid') ?? form.getAttribute('aria-label') ?? '' });
  }, true);
  on(document, 'visibilitychange', () => {
    capture('client.visibility', { state: document.visibilityState });
    if (document.visibilityState === 'hidden') beacon();
  });
  on(window, 'pagehide', () => { capture('client.lifecycle', { state: 'leaving' }); beacon(); });
  watchNotices();
  watchPerformance();
  if (socketNow) watchSocket(socketNow);
  every(SEND_MS, () => send(realFetch));
  changed();
}

function stop(): void {
  if (!recording) return;
  beacon();
  recording = null;
  queue = [];
  captureStop();
  for (const c of cleanups.splice(0)) c();
  changed();
}

/** The table's socket: says whether this table is being recorded, and only ever when it is or just stopped being. */
export function watchDiagnostics(socket: Socket, campaignId: string): () => void {
  socketNow = socket;
  const told = (raw: unknown) => {
    const s = raw as DiagState | null;
    if (s?.campaignId !== campaignId) return;
    if (s.fresh) captureForget();
    if (s.on && s.ticket) start(campaignId, s.ticket); else stop();
  };
  socket.on(TABLE_DIAG, told);
  // Recording already, from the early check: this socket's changes count too.
  if (recording) watchSocket(socket);
  return () => { socket.off(TABLE_DIAG, told); socketNow = null; stop(); };
}

/** The server answered the join, so it will say if this table is recorded. An error from now on needs no question. */
export function diagnosticsJoined(): void { known = true; }

/**
 * A page that failed before it reached its table (issue 1214 was a blank page) never hears the socket, so the first
 * error held then asks once whether the table is being recorded. Off, the answer ends it and nothing is sent.
 */
let asked = false;
onError(() => {
  if (asked || known || !hasSession()) return;
  const id = /^\/t\/([0-9a-f-]{36})/i.exec(window.location.pathname)?.[1];
  if (!id) return;
  asked = true;
  void authFetch(`/api/tables/${id}/diagnostics`)
    .then(async (res) => {
      const s = res.ok ? await res.json() as DiagState : null;
      if (s?.on && s.ticket) start(id, s.ticket);
    })
    .catch(() => undefined);
});

/** This recording's short hash for the DM's Debug mode, or null when the table is not being recorded. */
export function useDiagnosticsHash(): string | null {
  const read = () => { const session = recording?.ticket.split('.')[1]; return session ? diagHash(session) : null; };
  return useSyncExternalStore((l) => { listeners.add(l); return () => { listeners.delete(l); }; }, read, () => null);
}

/** The DM's Debug mode. True when the server took it; the socket then tells every browser, this one too. */
export async function setDiagnostics(campaignId: string, want: boolean): Promise<boolean> {
  try {
    const res = await authFetch(`/api/tables/${campaignId}/diagnostics`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ on: want }) });
    return res.ok;
  } catch { return false; }
}
